import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, alertsDb } from './supabase';
import type { Developer } from '@/types';

type AccessState = 'loading' | 'signed_out' | 'ok' | 'not_allowlisted' | 'error';

interface AuthValue {
  session: Session | null;
  user: User | null;
  developer: Developer | null;
  access: AccessState;
  errorMessage: string | null;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshDeveloper: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | undefined>(undefined);

// Provision (or fetch) the developer row for the current session. ensure_developer
// is allowlist-gated server-side and raises `not_allowlisted` for disallowed emails.
async function provision(): Promise<{ dev: Developer | null; state: AccessState; msg: string | null }> {
  const { data, error } = await alertsDb().rpc('ensure_developer', {});
  if (error) {
    if (error.message?.includes('not_allowlisted')) return { dev: null, state: 'not_allowlisted', msg: null };
    return { dev: null, state: 'error', msg: error.message };
  }
  return { dev: data as Developer, state: 'ok', msg: null };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [developer, setDeveloper] = useState<Developer | null>(null);
  const [access, setAccess] = useState<AccessState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function applySession(next: Session | null) {
    setSession(next);
    if (next) {
      // scope realtime to this user's JWT so RLS-filtered channels deliver
      supabase.realtime.setAuth(next.access_token);
      const { dev, state, msg } = await provision();
      setDeveloper(dev);
      setAccess(state);
      setErrorMessage(msg);
    } else {
      setDeveloper(null);
      setAccess('signed_out');
      setErrorMessage(null);
    }
  }

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) void applySession(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      // Ignore token-refresh churn; only react to real transitions.
      if (['SIGNED_IN', 'SIGNED_OUT', 'INITIAL_SESSION'].includes(event)) {
        void applySession(next);
      }
    });
    return () => { active = false; sub.subscription.unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthValue>(() => ({
    session,
    user: session?.user ?? null,
    developer,
    access,
    errorMessage,
    signInWithPassword: async (email, password) => {
      // Reuses the shared project's Email provider + auth.users (same accounts as
      // admin.neoseum.com). Access to the alerts tool is still gated by the
      // developer_allowlist inside ensure_developer().
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error?.message ?? null };
    },
    signOut: async () => { await supabase.auth.signOut(); },
    refreshDeveloper: async () => {
      const { dev, state, msg } = await provision();
      setDeveloper(dev); setAccess(state); setErrorMessage(msg);
    },
  }), [session, developer, access, errorMessage]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
